// /commissions — Commission Rates (PAGES.md §3.58)
//
// Configure percentage or flat commission rates per staff member, location, or
// category. OWNER+ can create/edit/deactivate; MANAGER sees read-only list.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  DataTable,
  Badge,
  Input,
  Skeleton,
  EmptyState,
  Modal,
  RadioGroup,
  ConfirmDialog,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { useAuthStore } from '@/stores/use-auth-store';
import { userService } from '@/services/user.service';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { formatPaise as formatPaiseCurrency } from '@/utils/currency';
import { paiseToRupeeInput, rupeeInputToPaise } from '@/utils/price-input';
import { getSubunitMultiplier } from '@bbpos/utils';
import { locationService } from '@/services/location.service';
import {
  useCommissionRates,
  useCommissionRateStats,
  useCreateRate,
  useUpdateRate,
  useDeactivateRate,
  RateTypeBadge,
  validateEffectiveDates,
} from '@/features/commissions';
import type { CommissionRate, CommissionRateType, CreateRateInput, UpdateRateInput, DateFieldErrors } from '@/features/commissions';

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CASHIER: 5,
};

const ACTIVE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

type Row = CommissionRate;

function formatRateValue(row: CommissionRate, currency: CurrencyCode): string {
  if (row.rate_type === 'PERCENTAGE') return `${row.rate_pct}%`;
  return formatPaiseCurrency(row.rate_value, currency);
}

function CommissionRatesContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canCreate = roleLevel >= ROLE_LEVELS.OWNER;
  // Per-item (Flat / Item) commission is a retail-only concept — it maps to a
  // per-product commission %, which restaurants (recipe-based) don't use.
  const isRestaurant = useAuthStore((s) => s.profile?.tenant?.business_type) === 'RESTAURANT';

  const page = Number(searchParams.get('page') ?? '1');
  const isActive = searchParams.get('is_active') ?? '';
  const search = searchParams.get('search') ?? '';

  // Search/filter run server-side so they span the whole tenant dataset, not
  // just the current page (issue #158). The table renders exactly what the
  // server returns for the active query.
  const { data, isLoading, isFetching } = useCommissionRates({
    page,
    is_active: isActive || undefined,
    search: search || undefined,
  });
  const rows = data?.data ?? [];

  // Hero badges show tenant-wide totals (independent of search/page) so the
  // "active" count is never undercounted by the current page (issue #158).
  const { data: statsData } = useCommissionRateStats();

  const createMutation = useCreateRate();
  const updateMutation = useUpdateRate();
  const deactivateMutation = useDeactivateRate();

  const { data: usersData } = useQuery({
    queryKey: ['commissions-users-picker'],
    queryFn: () => userService.list({ limit: 200, is_active: 'true' }),
    staleTime: 60_000,
  });
  const userOptions = useMemo(
    () => [
      // Role-wide targets first (value prefixed `role:` so submit can tell them
      // apart from a user id). These create one rate for every staff of that role.
      { value: 'role:CASHIER', label: 'All Cashiers' },
      { value: 'role:MANAGER', label: 'All Managers' },
      { value: 'role:OWNER', label: 'All Owners' },
      ...(usersData?.data ?? []).map((u) => ({
        value: u.id,
        label: `${u.name} — ${u.role}`,
      })),
    ],
    [usersData],
  );

  const { data: locationsData } = useQuery({
    queryKey: ['commissions-locations-picker'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locationOptions = useMemo(
    () => [
      { value: '', label: 'All locations' },
      ...(locationsData?.data ?? []).map((l) => ({
        value: l.id,
        label: l.location_code ? `${l.name} — ${l.location_code}` : l.name,
      })),
    ],
    [locationsData],
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<CommissionRate | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<string | null>(null);

  // Form state
  const [userId, setUserId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [rateType, setRateType] = useState<CommissionRateType>('PERCENTAGE');
  const [rateValue, setRateValue] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [dateErrors, setDateErrors] = useState<DateFieldErrors>({});
  // FLAT_PER_BILL: minimum bill total (rupees) above which the commission applies.
  const [minBillAmount, setMinBillAmount] = useState('');
  // FLAT_PER_ITEM: informational disclaimer popup.
  const [showItemDisclaimer, setShowItemDisclaimer] = useState(false);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  function openCreate() {
    setEditingRate(null);
    setUserId('');
    setLocationId('');
    setRateType('PERCENTAGE');
    setRateValue('');
    setEffectiveFrom(new Date().toISOString().slice(0, 10));
    setEffectiveTo('');
    setDateErrors({});
    setMinBillAmount('');
    setShowItemDisclaimer(false);
    setModalOpen(true);
  }

  function openEdit(rate: CommissionRate) {
    setEditingRate(rate);
    setRateType(rate.rate_type);
    // FLAT rates store rate_value in paise → show as rupees; PERCENTAGE is a raw percent.
    setRateValue(rate.rate_type === 'PERCENTAGE' ? String(rate.rate_value) : paiseToRupeeInput(rate.rate_value, currency));
    setEffectiveFrom(rate.effective_from);
    setEffectiveTo(rate.effective_to ?? '');
    setDateErrors({});
    setModalOpen(true);
  }

  // Keyboard: n → create (OWNER), / → focus search
  const canCreateRef = useRef(canCreate);
  canCreateRef.current = canCreate;
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'n' && canCreateRef.current) { e.preventDefault(); openCreate(); }
      if (e.key === '/') {
        e.preventDefault();
        (document.getElementById('commission-search') as HTMLInputElement | null)?.focus();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleSave = useCallback(() => {
    // PERCENTAGE: raw percent. FLAT: user types rupees → convert to paise for storage.
    const val = rateType === 'PERCENTAGE' ? Number(rateValue) : (rupeeInputToPaise(rateValue, currency) ?? NaN);
    if (!Number.isFinite(val) || val < 0) {
      toast({ variant: 'warning', title: 'Invalid rate value' });
      return;
    }

    // Catch malformed dates / end-before-start inline so we never fire a
    // request the server would only reject with a generic error (issue #157).
    const dateErrs = validateEffectiveDates(effectiveFrom, effectiveTo, { requireFrom: !editingRate });
    setDateErrors(dateErrs);
    if (dateErrs.effective_from || dateErrs.effective_to) return;

    if (editingRate) {
      updateMutation.mutate(
        { id: editingRate.id, input: { rate_value: val, effective_to: effectiveTo || undefined } as UpdateRateInput },
        {
          onSuccess: () => { toast({ variant: 'success', title: 'Rate updated' }); setModalOpen(false); },
          onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
        },
      );
    } else {
      if (!userId) { toast({ variant: 'warning', title: 'Select a staff member or a role' }); return; }
      // A `role:XXX` selection targets every staff of that role; otherwise it's a user id.
      const isRoleTarget = userId.startsWith('role:');
      const roleTarget = isRoleTarget ? userId.slice(5) : undefined;

      // FLAT_PER_BILL: a minimum bill total (above which the commission applies).
      let minBillPaise: number | undefined;
      if (rateType === 'FLAT_PER_BILL') {
        minBillPaise = rupeeInputToPaise(minBillAmount, currency) ?? NaN;
        if (!Number.isFinite(minBillPaise) || minBillPaise < 0) {
          toast({ variant: 'warning', title: 'Enter a valid minimum bill amount' });
          return;
        }
      }

      const input: CreateRateInput = {
        ...(isRoleTarget
          ? { role: roleTarget as 'OWNER' | 'MANAGER' | 'CASHIER' }
          : { user_id: userId }),
        location_id: locationId || undefined,
        rate_type: rateType,
        rate_value: val,
        effective_from: effectiveFrom,
        effective_to: effectiveTo || undefined,
        ...(minBillPaise !== undefined ? { min_bill_amount: minBillPaise } : {}),
      };
      createMutation.mutate(input, {
        onSuccess: () => { toast({ variant: 'success', title: 'Rate created' }); setModalOpen(false); },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      });
    }
  }, [editingRate, userId, locationId, rateType, rateValue, minBillAmount, effectiveFrom, effectiveTo, createMutation, updateMutation, toast, currency]);

  const handleDeactivate = useCallback(() => {
    if (!confirmDeactivate) return;
    deactivateMutation.mutate(confirmDeactivate, {
      onSuccess: () => { toast({ variant: 'success', title: 'Rate deactivated' }); setConfirmDeactivate(null); },
      onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
    });
  }, [confirmDeactivate, deactivateMutation, toast]);

  const columns: ColumnDef<Row>[] = useMemo(
    () => [
      {
        key: 'user_id' as keyof Row,
        header: 'Staff',
        render: (_v: unknown, row: Row) => {
          const label = row.user_name ?? (row.user_id ? row.user_id.slice(0, 8) : row.role ? `All ${row.role}s` : '—');
          return canCreate ? (
            <button type="button" onClick={() => openEdit(row)} className="group text-left -my-1 rounded-lg px-2 py-1 transition-colors hover:bg-violet-50 dark:hover:bg-violet-900/20">
              <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-violet-700 dark:group-hover:text-violet-300">
                {label}
              </p>
            </button>
          ) : (
            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
              {label}
            </p>
          );
        },
      },
      {
        key: 'rate_type' as keyof Row,
        header: 'Type',
        render: (_v: unknown, row: Row) => <RateTypeBadge type={row.rate_type} />,
      },
      {
        key: 'rate_value' as keyof Row,
        header: 'Rate',
        numeric: true,
        render: (_v: unknown, row: Row) => (
          <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {formatRateValue(row, currency)}
          </span>
        ),
      },
      {
        key: 'location_id' as keyof Row,
        header: 'Location',
        render: (_v: unknown, row: Row) => (
          <span className="text-sm text-neutral-700 dark:text-neutral-200">
            {row.location_name ?? (row.location_id ? row.location_id.slice(0, 8) : 'All locations')}
          </span>
        ),
      },
      {
        key: 'effective_from' as keyof Row,
        header: 'Effective',
        render: (_v: unknown, row: Row) => (
          <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
            {row.effective_from}{row.effective_to ? ` → ${row.effective_to}` : ' → ongoing'}
          </span>
        ),
      },
      {
        key: 'is_active' as keyof Row,
        header: 'Status',
        render: (_v: unknown, row: Row) => (
          <Badge variant={row.is_active ? 'success' : 'voided'} size="sm" dot>
            {row.is_active ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      ...(canCreate ? [{
        key: 'actions' as keyof Row,
        header: '',
        render: (_v: unknown, row: Row) => row.is_active ? (
          <Button variant="danger" onClick={() => setConfirmDeactivate(row.id)} className="!text-white !bg-danger-500 hover:!bg-danger-600 text-xs px-2 py-1">
            Deactivate
          </Button>
        ) : null,
      }] : []),
    ],
    [canCreate, currency],
  );

  const totalCount = statsData?.total ?? 0;
  const activeCount = statsData?.active ?? 0;

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(139,92,246,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              Staff Compensation
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Commission Rates</h1>
            <p className="mt-1.5 text-sm text-white/90 max-w-xl">
              Configure percentage or flat commission rates per staff member, location, or category.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
                {totalCount.toLocaleString('en-IN')} total
              </span>
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
                {activeCount} active
              </span>
            </div>
          </div>
          {canCreate ? (
            <Button variant="primary" onClick={openCreate} className="!bg-white !text-violet-700 hover:!bg-white/90 !border-transparent">
              + Add Rate
            </Button>
          ) : null}
        </div>
      </div>

      {/* Filter bar */}
      <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4 sm:p-5 ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 mb-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="flex-1 min-w-0">
            <Input
              id="commission-search"
              variant="search"
              placeholder="Search by staff name (press / to focus)"
              value={search}
              onChange={(v) => setParam('search', v)}
              aria-label="Search commission rates"
            />
          </div>
          <div className="w-full lg:w-44">
            <Select options={ACTIVE_OPTIONS} value={isActive} onChange={(v) => setParam('is_active', typeof v === 'string' ? v : '')} />
          </div>
        </div>
      </div>

      {/* Data */}
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}</div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 animate-fade-in">
          <EmptyState variant="page" title="No commission rates" description="Configure rates so staff earn commissions on their sales."
            action={canCreate ? { label: '+ Add Rate', onClick: openCreate } : undefined}
            icon={<svg className="h-20 w-20 text-violet-300 dark:text-violet-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up">
          <DataTable<Row> columns={columns} data={rows} loading={isFetching} totalCount={data?.meta.total} currentPage={page} pageSize={20} onPageChange={(p) => setParam('page', String(p))} stickyHeader />
        </section>
      )}

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingRate ? 'Edit Rate' : 'Add Commission Rate'} variant="wide">
        <div className="space-y-4">
          {!editingRate ? (
            <>
              <Select
                label="Staff Member"
                variant="searchable"
                options={userOptions}
                value={userId}
                onChange={(v) => setUserId(typeof v === 'string' ? v : '')}
                placeholder="Search staff by name…"
                required
              />
              <Select
                label="Location (optional)"
                variant="searchable"
                options={locationOptions}
                value={locationId}
                onChange={(v) => setLocationId(typeof v === 'string' ? v : '')}
                placeholder="All locations"
                clearable
              />
              <RadioGroup label="Rate Type" value={rateType}
                onChange={(v) => {
                  const next = v as CommissionRateType;
                  setRateType(next);
                  // Flat / Item commission is configured per-product — inform the user.
                  if (next === 'FLAT_PER_ITEM') setShowItemDisclaimer(true);
                }}
                variant="horizontal"
                options={[
                  { value: 'PERCENTAGE', label: 'Percentage' },
                  { value: 'FLAT_PER_BILL', label: 'Flat / Bill' },
                  // Flat / Item is retail-only (per-product commission %).
                  ...(isRestaurant ? [] : [{ value: 'FLAT_PER_ITEM', label: 'Flat / Item' }]),
                ]}
              />
            </>
          ) : (
            <div className="flex items-center gap-2">
              <RateTypeBadge type={editingRate.rate_type} />
              <span className="text-xs text-neutral-500 dark:text-neutral-400">Type and user are immutable</span>
            </div>
          )}

          <Input
            label={rateType === 'PERCENTAGE' ? 'Rate (%)' : `Rate (${getCurrencySymbol(currency)})`}
            type="number"
            min={0}
            value={rateValue}
            onChange={setRateValue}
            helperText={rateType === 'PERCENTAGE' ? 'e.g. 5 = 5%' : `e.g. 5 = ${formatPaiseCurrency(5 * getSubunitMultiplier(currency), currency)}`}
            required
          />

          {/* Bill-level commission: minimum bill total above which it applies. */}
          {!editingRate && rateType === 'FLAT_PER_BILL' && (
            <Input
              label={`Minimum Bill Amount (${getCurrencySymbol(currency)})`}
              type="number"
              min={0}
              value={minBillAmount}
              onChange={setMinBillAmount}
              helperText="Commission applies only to bills above this amount."
              required
            />
          )}

          {!editingRate ? (
            <Input
              label="Effective From"
              type="date"
              value={effectiveFrom}
              onChange={(v) => { setEffectiveFrom(v); setDateErrors((e) => ({ ...e, effective_from: undefined })); }}
              placeholder="YYYY-MM-DD"
              error={dateErrors.effective_from}
              required
            />
          ) : (
            <Input
              label="Effective From"
              value={effectiveFrom}
              disabled
              helperText="Start date is fixed once a rate is created — deactivate and add a new rate to change it."
            />
          )}
          <Input
            label="Effective To (optional)"
            type="date"
            value={effectiveTo}
            onChange={(v) => { setEffectiveTo(v); setDateErrors((e) => ({ ...e, effective_to: undefined })); }}
            placeholder="YYYY-MM-DD (blank = ongoing)"
            error={dateErrors.effective_to}
          />

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={createMutation.isPending || updateMutation.isPending}>
              {editingRate ? 'Save Changes' : 'Create Rate'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Flat / Item disclaimer — per-item commission is set on the product page */}
      <Modal
        open={showItemDisclaimer}
        onClose={() => setShowItemDisclaimer(false)}
        title="Per-item commission"
        footer={
          <div className="flex items-center justify-end">
            <Button variant="primary" onClick={() => setShowItemDisclaimer(false)}>OK</Button>
          </div>
        }
      >
        <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-2">
          <p>
            Item-level commission is set <span className="font-semibold">per product</span>, on the
            product detail page &mdash; not here.
          </p>
          <p>To set it:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Go to <span className="font-semibold">Products</span> and open a product.</li>
            <li>On the product detail page, enter the <span className="font-semibold">Commission (%)</span> field (next to the GST fields).</li>
            <li><span className="font-semibold">Save</span> the product.</li>
          </ol>
          <p>Click <span className="font-semibold">OK</span> to continue.</p>
        </div>
      </Modal>

      {/* Deactivate Confirm */}
      <ConfirmDialog
        open={Boolean(confirmDeactivate)}
        onCancel={() => setConfirmDeactivate(null)}
        onConfirm={handleDeactivate}
        title="Deactivate Rate"
        description="This rate will no longer apply to future commission calculations. Existing settlements are not affected."
        variant="warning"
        loading={deactivateMutation.isPending}
      />
    </div>
  );
}

export function CommissionRatesPage() {
  return (
    <PageContainer title="Commission Rates" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Commissions' }]}>
      <ErrorBoundary resetKey="commission-rates">
        <CommissionRatesContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
